/**
 * AdaptiveDialog
 *
 * Renders a Radix Dialog on desktop (>=768px) and a vaul Drawer (bottom sheet)
 * on mobile (<768px). Uses `useIsMobile()` to detect the viewport at runtime.
 */

import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Drawer } from "vaul";
import { useIsMobile } from "@/lib/use-media";
import { cn } from "@/lib/cn";
import { SCRIM_CLASS } from "@/components/premium";

interface AdaptiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  /**
   * Keep the accessible name (Radix/vaul Title) but render it screen-reader
   * only — for dialogs whose CONTENT already renders its own visible heading
   * (e.g. a conversation thread embedded as a pop-up), where a second visible
   * title would duplicate it. Ignored when `title` is absent.
   */
  titleVisuallyHidden?: boolean;
  description?: string;
  descriptionClassName?: string;
  children: ReactNode;
  /**
   * Desktop dialog width — "md" (default, `max-w-md`) fits a confirmation or a
   * short form; "lg" (`max-w-2xl`) fits content with several labelled sections
   * (e.g. the activity row-detail pop-up); "xl" (`max-w-4xl`, full 85dvh) fits
   * a whole embedded surface (e.g. a conversation thread opened as a pop-up
   * over the inbox). Mobile ignores this — the Drawer is always full-width.
   */
  size?: "md" | "lg" | "xl";
  /** Raises a recovery chooser above the persistent Forensic Pause banner. */
  layer?: "default" | "recovery";
}

type MobileViewportStyle = CSSProperties & {
  "--adaptive-dialog-bottom": string;
  "--adaptive-dialog-max-height": string;
};

export const MOBILE_DRAWER_MOTION_MS = 300;

/**
 * True inside an already-open AdaptiveDialog. Content hosted in a dialog can
 * itself mount another AdaptiveDialog (e.g. a conversation thread pop-up whose
 * composer opens the ordered-approvals wizard, or the members sheet) — on
 * mobile, vaul requires a nested drawer to use `Drawer.NestedRoot`, not a
 * second `Drawer.Root` (adversarial review finding 3). This context makes that
 * automatic for every consumer instead of a per-call-site prop.
 */
const AdaptiveDialogNestingContext = createContext(false);

export function AdaptiveDialog({
  open,
  onOpenChange,
  title,
  titleVisuallyHidden = false,
  description,
  descriptionClassName,
  children,
  size = "md",
  layer = "default",
}: AdaptiveDialogProps) {
  const titleClass = titleVisuallyHidden
    ? "sr-only"
    : "text-[length:var(--text-heading-size)] font-semibold text-text-primary mb-1";
  const isMobile = useIsMobile();
  const isNested = useContext(AdaptiveDialogNestingContext);
  const [mobileViewportStyle, setMobileViewportStyle] =
    useState<MobileViewportStyle>({
      "--adaptive-dialog-bottom": "0px",
      "--adaptive-dialog-max-height": "95dvh",
    });

  // Keep the mobile sheet above the software keyboard. `dvh` follows browser
  // chrome but is not consistently reduced by the on-screen keyboard (notably
  // on iOS); VisualViewport reports the actually visible region.
  useLayoutEffect(() => {
    const viewport = window.visualViewport;
    if (!isMobile || !open) return undefined;
    if (!viewport) {
      setMobileViewportStyle({
        "--adaptive-dialog-bottom": "0px",
        "--adaptive-dialog-max-height": "95dvh",
      });
      return undefined;
    }

    const updateViewportStyle = (): void => {
      const keyboardInset = Math.max(
        0,
        window.innerHeight - viewport.height - viewport.offsetTop,
      );
      setMobileViewportStyle({
        "--adaptive-dialog-bottom": `${Math.round(keyboardInset)}px`,
        "--adaptive-dialog-max-height": `${Math.floor(viewport.height * 0.95)}px`,
      });
    };

    updateViewportStyle();
    viewport.addEventListener("resize", updateViewportStyle);
    viewport.addEventListener("scroll", updateViewportStyle);
    return () => {
      viewport.removeEventListener("resize", updateViewportStyle);
      viewport.removeEventListener("scroll", updateViewportStyle);
    };
  }, [isMobile, open]);

  // ---------------------------------------------------------------------------
  // Trigger-focus restore (Task 9, AC-8) — additive, applies to BOTH branches.
  //
  // On open (false→true) capture the currently-focused element; on close
  // (true→false) refocus it on the next frame, once the portal has unmounted.
  // Radix Dialog already restores focus to its trigger on desktop, so this is a
  // harmless no-op there (refocusing the same element twice is fine); it closes
  // the vaul Drawer gap on mobile (vaul does NOT restore focus, no prop for it).
  //
  // Only fires on a genuine true→false transition — never steals focus on mount.
  // ---------------------------------------------------------------------------
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const openRef = useRef(open);
  const prevOpenRef = useRef(open);

  // Continuously track the last element to receive focus WHILE THE DIALOG IS
  // CLOSED, via a capture-phase focusin listener mounted once. The open trigger
  // is the last thing focused before `open` flips true, so this captures it
  // reliably — unlike reading document.activeElement in the open-transition
  // effect, which races the click's own focus changes (e.g. a button that
  // blurs+remounts) and the overlay's autofocus. We ignore focusin events that
  // arrive while open, so an element inside the overlay never overwrites the
  // captured trigger.
  openRef.current = open;
  useLayoutEffect(() => {
    const onFocusIn = () => {
      if (openRef.current) return; // ignore focus moves inside the open overlay
      const active = document.activeElement as HTMLElement | null;
      if (active && active !== document.body) lastFocusedRef.current = active;
    };
    if (!open) onFocusIn(); // seed once on mount when starting closed
    document.addEventListener("focusin", onFocusIn, true);
    return () => document.removeEventListener("focusin", onFocusIn, true);
    // Mount once — the openRef guard handles the open/closed gating, so this
    // effect must NOT re-run on `open` (re-running would re-seed mid-close and
    // capture the still-focused, about-to-unmount overlay element).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On a genuine true→false transition, refocus the captured trigger on the
  // next frame (after the portal has unmounted). Radix already restores focus
  // on desktop, so this is a harmless no-op there; it closes the vaul Drawer
  // gap on mobile (vaul does NOT restore focus, and exposes no prop for it).
  // Never steals focus on mount — only fires when open was previously true.
  useLayoutEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (!(wasOpen && !open)) return undefined;

    const el = lastFocusedRef.current;
    if (el && typeof el.focus === "function" && el.isConnected) {
      const raf = requestAnimationFrame(() => {
        if (el.isConnected) el.focus();
      });
      return () => cancelAnimationFrame(raf);
    }
    return undefined;
  }, [open]);

  if (isMobile) {
    // vaul: a drawer opened from INSIDE another drawer must be a NestedRoot —
    // a second Root fights the parent over body scroll-locking/transforms.
    const DrawerRoot = isNested ? Drawer.NestedRoot : Drawer.Root;
    return (
      <DrawerRoot
        open={open}
        onOpenChange={onOpenChange}
        // AdaptiveDialog owns keyboard positioning via VisualViewport below.
        // Disable vaul's competing inline height/bottom writes so resize order
        // cannot leave the sheet under the keyboard.
        repositionInputs={false}
      >
        <Drawer.Portal>
          <Drawer.Overlay
            style={{
              animationDuration: `${MOBILE_DRAWER_MOTION_MS}ms`,
              transitionDuration: `${MOBILE_DRAWER_MOTION_MS}ms`,
            }}
            className={cn(
              "fixed inset-0 motion-reduce:!animate-none motion-reduce:!transition-none",
              layer === "recovery" ? "z-[70]" : "z-40",
              SCRIM_CLASS,
            )}
            data-testid="adaptive-dialog-overlay"
          />
          <Drawer.Content
            aria-modal="true"
            style={{
              ...mobileViewportStyle,
              animationDuration: `${MOBILE_DRAWER_MOTION_MS}ms`,
              transitionDuration: `${MOBILE_DRAWER_MOTION_MS}ms`,
            }}
            className={cn(
              "fixed left-0 right-0",
              layer === "recovery" ? "z-[80]" : "z-50",
              "motion-reduce:!animate-none motion-reduce:!transition-none",
              "bottom-[var(--adaptive-dialog-bottom)]",
              "bg-bg-elevated border-t border-border-default",
              "rounded-t-[var(--radius-lg)] p-4",
              "pb-[calc(16px+env(safe-area-inset-bottom))]",
              // DR-2 (#1581): tall sheets must scroll, not clip.
              // Keep overflow off Drawer.Content itself because vaul's
              // drag-fill pseudo-element otherwise becomes blank, scrollable
              // content below the form. The inner viewport scrolls instead.
              "max-h-[var(--adaptive-dialog-max-height)] flex flex-col",
            )}
          >
            <div
              data-testid="adaptive-dialog-scroll"
              className="min-h-0 overflow-y-auto overscroll-contain"
            >
              <div className="mx-auto w-10 h-1 rounded-full bg-border-default mb-4" />
              {title && <Drawer.Title className={titleClass}>{title}</Drawer.Title>}
              {description && (
                <Drawer.Description className={cn("text-[length:var(--text-caption-size)] text-text-secondary mb-4", descriptionClassName)}>
                  {description}
                </Drawer.Description>
              )}
              <AdaptiveDialogNestingContext.Provider value={true}>
                {children}
              </AdaptiveDialogNestingContext.Provider>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </DrawerRoot>
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn("fixed inset-0", layer === "recovery" ? "z-[70]" : "z-40", SCRIM_CLASS)}
          data-testid="adaptive-dialog-overlay"
        />
        <Dialog.Content
          aria-modal="true"
          {...(description ? {} : { "aria-describedby": undefined })}
          className={cn(
            "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
            layer === "recovery" ? "z-[80]" : "z-50",
            "bg-bg-elevated border border-border-default",
            "rounded-[var(--radius-lg)] p-5",
            "shadow-[shadow:var(--elevation-3)]",
            "w-full",
            // DR-1 (#1581): BOTH sizes cap at the viewport and scroll — the
            // default "md" previously had no max-h/overflow, so tall content
            // (e.g. Add-a-guardian) clipped off-screen with no way to reach it.
            "max-h-[85dvh] overflow-y-auto",
            // "xl" additionally pins the HEIGHT (not just a cap): an embedded
            // thread surface manages its own internal scroll + sticky chrome,
            // and a height that collapses to content would bounce as messages
            // stream in.
            size === "xl" ? "max-w-4xl h-[85dvh]" : size === "lg" ? "max-w-2xl" : "max-w-md",
            "focus:outline-none",
          )}
        >
          {title && <Dialog.Title className={titleClass}>{title}</Dialog.Title>}
          {description && (
            <Dialog.Description className={cn("text-[length:var(--text-caption-size)] text-text-secondary mb-4", descriptionClassName)}>
              {description}
            </Dialog.Description>
          )}
          {/* Desktop Radix stacks nested Dialog.Roots fine — the provider is
              still set so a nested MOBILE drawer under any parent is handled. */}
          <AdaptiveDialogNestingContext.Provider value={true}>
            {children}
          </AdaptiveDialogNestingContext.Provider>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
